import { Dialog } from '@headlessui/react';
import { useState } from 'react';

import type { ScenarioStepTimingV1 } from 'api/scenarios';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import { Modal } from 'components/Modal';

import { DurationField } from '../DurationField';
import { formatMinuteOfDay, parseMinuteOfDay } from '../duration';

/**
 * 画面の選択肢。保存するmodeは3値のままで、「登録直後」はelapsedの待機0へ正規化する。
 * `relative`は前のステップが存在する2つ目以降でだけ選べる。
 * 逆に「登録直後」は先頭stepでだけ選べる。2つ目以降は前のstepを待ってから送るため、
 * 選んでも`relative`の待機0と同じ意味にしかならず、「登録直後」という表示だけが実挙動とずれる。
 */
type Choice = 'immediate' | 'absolute' | 'elapsed' | 'relative';

type Props = {
  open: boolean;
  /** 何番目のステップか（1始まり）。読み上げ名が他のステップと重ならないようにする。 */
  step: number;
  value: ScenarioStepTimingV1 | null;
  onClose: () => void;
  onDecide: (timing: ScenarioStepTimingV1) => void;
};

const cardClass = 'grid gap-2 rounded-2xl border p-4 text-sm';
const fieldRowClass = 'flex flex-wrap items-end gap-2 rounded-xl bg-neutral-25 p-3';
// 迷いやすい点は折りたたみへ逃がさず、選択肢のすぐ下へ置く
const noteClass = 'text-xs text-muted';

/**
 * 保存済みtimingを画面の選択肢と各欄の初期値へ戻す。
 * elapsedの待機0だけ「登録直後」として復元し、先頭stepのrelativeは選択肢が無いため
 * 待機値を保ったまま「経過時間で送る」として扱う。
 * 2つ目以降のelapsed待機0は「登録直後」の選択肢を持たないため、同じ意味の
 * 「前のステップから」待機0へ読み替える（既存の下書き・公開版をそのまま開けるようにする）。
 */
function restore(value: ScenarioStepTimingV1 | null, isFirst: boolean) {
  const blank = { choice: null as Choice | null, elapsed: null as number | null, delay: null as number | null, day: null as number | null, minute: null as number | null };
  if (!value) return blank;
  if (value.mode === 'absolute_time') return { ...blank, choice: 'absolute' as const, day: value.dayOffset, minute: value.minuteOfDay };
  if (value.mode === 'relative') {
    if (!isFirst) return { ...blank, choice: 'relative' as const, delay: value.delayMinutes };
    return value.delayMinutes === 0 ? { ...blank, choice: 'immediate' as const } : { ...blank, choice: 'elapsed' as const, elapsed: value.delayMinutes };
  }
  if (value.elapsedMinutes !== 0) return { ...blank, choice: 'elapsed' as const, elapsed: value.elapsedMinutes };
  return isFirst ? { ...blank, choice: 'immediate' as const } : { ...blank, choice: 'relative' as const, delay: 0 };
}

export function TimingModal({ open, step, value, onClose, onDecide }: Props) {
  const isFirst = step === 1;
  const initial = restore(value, isFirst);
  const [choice, setChoice] = useState<Choice | null>(initial.choice);
  const [elapsedMinutes, setElapsedMinutes] = useState<number | null>(initial.elapsed);
  const [delayMinutes, setDelayMinutes] = useState<number | null>(initial.delay);
  const [dayOffset, setDayOffset] = useState<number | null>(initial.day);
  const [minuteOfDay, setMinuteOfDay] = useState<number | null>(initial.minute);
  const [showHelp, setShowHelp] = useState(false);

  const decided = ((): ScenarioStepTimingV1 | null => {
    if (choice === 'immediate') return { mode: 'elapsed', elapsedMinutes: 0 };
    if (choice === 'elapsed') return elapsedMinutes === null ? null : { mode: 'elapsed', elapsedMinutes };
    if (choice === 'relative') return delayMinutes === null ? null : { mode: 'relative', delayMinutes };
    if (choice === 'absolute') return dayOffset === null || minuteOfDay === null ? null : { mode: 'absolute_time', dayOffset, minuteOfDay };
    return null;
  })();

  const option = (key: Choice, title: string, description: string, body?: React.ReactNode) => (
    <label className={`${cardClass} ${choice === key ? 'border-accent bg-accent-soft' : 'border-border/70'}`}>
      <span className="flex items-start gap-3">
        <input type="radio" className="mt-1" aria-label={title} name={`timing-${step}`} checked={choice === key} onChange={() => setChoice(key)} />
        <span className="grid gap-1">
          <span className="font-semibold text-ink">{title}</span>
          <span className="text-xs text-muted">{description}</span>
        </span>
      </span>
      {body}
    </label>
  );

  return (
    <Modal open={open} onClose={onClose} size="lg">
      <div className="grid gap-4">
        <div className="grid gap-1 text-center">
          <Dialog.Title className="text-lg font-semibold text-ink">配信タイミング選択</Dialog.Title>
          <button type="button" className="text-xs text-accent underline" onClick={() => setShowHelp((shown) => !shown)}>
            配信タイミングの違いがよく分からない方はこちら
          </button>
        </div>

        {showHelp && (
          <div className="grid gap-3 rounded-2xl bg-neutral-25 p-4 text-xs text-ink">
            <p>「登録」とは、その友だちがこのステップ配信に入った時点のことです。友だち追加・タグ付与・手動登録のどれで入っても同じです。</p>
            <p className="font-semibold">3つのステップすべてに「1日」と入れた場合、届き方はこう変わります。</p>
            <ul className="grid gap-1 pl-4">
              <li className="list-disc">
                <span className="font-semibold">経過時間で送る</span>：3通とも登録の1日後に届きます（起点がどれも登録のため）
              </li>
              <li className="list-disc">
                <span className="font-semibold">前のステップから</span>：1日後・2日後・3日後と、1日おきに届きます
              </li>
            </ul>
            <p>途中にステップを足すときも、「前のステップから」なら足した1つを設定するだけで済み、後ろのステップを直す必要がありません。</p>
          </div>
        )}

        <div className="grid gap-3">
          {isFirst &&
            option(
              'immediate',
              '登録直後',
              '登録された時点ですぐに送信します',
              <span className={noteClass}>※ 友だち追加・タグ付与・手動登録のどれで登録されても、その時点で送ります</span>,
            )}
          {option(
            'absolute',
            '時刻を決めて送る',
            '登録からの経過日数と、送る時刻で指定します',
            <span className="grid gap-1">
              <span className={fieldRowClass}>
                <span className="text-xs text-muted">登録から</span>
                <Input className="w-20" type="number" min={0} aria-label={`ステップ ${step} 登録からの日数`} value={dayOffset ?? ''} onChange={(event) => { setChoice('absolute'); setDayOffset(event.target.value === '' ? null : Number(event.target.value)); }} />
                <span className="text-xs text-muted">日後の</span>
                <Input className="w-28" type="time" aria-label={`ステップ ${step} 配信時刻（日本時間）`} value={formatMinuteOfDay(minuteOfDay) ?? ''} onChange={(event) => { setChoice('absolute'); setMinuteOfDay(parseMinuteOfDay(event.target.value)); }} />
                <span className="text-xs text-muted">に配信する</span>
              </span>
              <span className={noteClass}>※ 時刻は日本時間で保存・配信します（見ている端末の時差に影響されません）</span>
              <span className={noteClass}>※ 登録当日に送る場合は「0日後」を選んでください</span>
              <span className={noteClass}>※ 予定時刻を過ぎてから登録された人には、翌日へ繰り越さずすぐに送ります</span>
            </span>,
          )}
          {option(
            'elapsed',
            '経過時間で送る（時刻は決まらない）',
            '登録からの経過時間で指定します。送る時刻は決まりません',
            <span className="grid gap-1">
              {/* Tabで通過しただけで方式が変わると、キーボード操作で意図と違う設定のまま決定できてしまう。
                  切り替えは値を入れた時だけにする */}
              <span className={fieldRowClass}>
                <DurationField step={step} legend="登録してから" trailing="後に配信する" value={elapsedMinutes} onChange={(value) => { setChoice('elapsed'); setElapsedMinutes(value); }} />
              </span>
              <span className={noteClass}>※ 何時に送るかを決めたい場合は「時刻を決めて送る」を選んでください</span>
            </span>,
          )}
          {!isFirst &&
            option(
              'relative',
              '前のステップから',
              '前のステップが送られてからの経過時間で指定します',
              <span className="grid gap-1">
                <span className={fieldRowClass}>
                  <DurationField step={step} legend="前のステップから" showLegend={false} trailing="後に配信する" value={delayMinutes} onChange={(value) => { setChoice('relative'); setDelayMinutes(value); }} />
                </span>
                <span className={noteClass}>※ 各ステップに同じ値を入れると、その間隔で順に届きます。「経過時間で送る」はどのステップも登録が起点です</span>
              </span>,
            )}
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="button" disabled={!decided} onClick={() => decided && onDecide(decided)}>
            決定
          </Button>
        </div>
      </div>
    </Modal>
  );
}
